import { ref, computed } from 'vue'

import DataSource from 'devextreme/data/data_source'
import ArrayStore from 'devextreme/data/array_store'

import { useWebSiteStore } from '@/stores/web-site.store'
import { hasValidationControls, useImportRowValidation } from '@/composables/import-row-validation'

export const mergeValidatedRow = (row, validatedRow, patchFields = null) => ({
  ...row,
  ...(validatedRow || {}),
  document_row_type: validatedRow?.document_row_type ?? row.document_row_type,
  keep_price_discount: row.keep_price_discount,
  ...(patchFields ? { patch_fields: patchFields } : {}),
})

export const findValidatedRow = (results, row) => {
  const matches = (results || []).filter(i => i?.id === row.id)
  if (matches.length <= 1) return matches[0]
  return matches.find(i => i.source === row.source) ?? matches[0]
}

const groupBySource = (rows, data) => rows.reduce((acc, row) => {
  (acc[row.source] ??= []).push(data(row))
  return acc
}, {})

const externalRowFields = [
  'source',
  'id',
  'parent',
  'group_name',
  'ref',
  'notes',
  'code',
  'name',
  'description',
  'supplier_brand_code',
  'quantity',
  'quantity_minutes',
  'suggested_time',
  'price',
  'vat_type_ref',
  'vat_type_code',
  'vat_type_percentage',
  'total_no_vat',
  'total_vat',
  'source_includes_vat',
  'document_row_type',
  'preserve_source_document_row_type',
]

const buildSourceRow = (row) => Object.fromEntries(
  externalRowFields
    .filter(field => row[field] !== undefined)
    .map(field => [field, row[field]])
)

const buildDryRunSource = (row, patchFields, includeSourceRow = false) => ({
  id: row.id,
  patch_fields: patchFields,
  ...(includeSourceRow ? { sourceRow: buildSourceRow(row) } : {}),
})

export const buildSelectionSources = (rows, includeSourceRow = () => false) => groupBySource(
  rows,
  row => buildDryRunSource(row, { quantity: row.quantity }, includeSourceRow(row.source))
)

const createIsExternalCatalogSource = (data, webSiteStore) => {
  const externalCatalogSources = [
    webSiteStore.importRowTypesEnum.CATALOG,
    webSiteStore.importRowTypesEnum.LRW,
    webSiteStore.importRowTypesEnum.AUTODATA,
  ]

  return (source) => !data.isDocumentSource && externalCatalogSources.includes(source)
}

export const buildImportSources = (rows, defaultAction = null) =>
  groupBySource(rows, row => {
    const sourceRow = {
      id: row.id,
      row_actions: (row.validations || []).map(validation => ({
        id: validation.id,
        action: validation.validation_action ?? defaultAction,
      })),
      keepPriceDiscount: row.keep_price_discount,
      quantity: row.quantity,
      total_vat: row.total_vat,
      total_no_vat: row.total_no_vat,
      custom_product_code: row.code,
    }

    if (row.patch_fields && Object.keys(row.patch_fields).length) {
      sourceRow.patch_fields = row.patch_fields
    }

    return sourceRow
  })

export const useImportRowApi = (data) => {
  const webSiteStore = useWebSiteStore()
  const isExternalCatalogSource = createIsExternalCatalogSource(data, webSiteStore)

  const importRowsAsync = ({ sources, isMove = false, isDryRun = false, firstLoad = false, allowedDocumentRowTypes = null, createNewAutodataProcessing = false }) =>
    webSiteStore.api.importClipboardRowsAsync({
      documentType: data.documentType,
      documentId: data.documentId,
      isDocumentSource: Boolean(data.isDocumentSource),
      sources,
      isMove,
      isDryRun,
      firstLoad,
      allowedDocumentRowTypes,
      createNewAutodataProcessing,
    })

  const revalidateRowAsync = async(row, patch, allowedDocumentRowTypes = null) => {
    const patchFields = { ...(row.patch_fields || {}), ...patch }

    const response = await importRowsAsync({
      sources: { [row.source]: [buildDryRunSource(row, patchFields, isExternalCatalogSource(row.source))] },
      isDryRun: true,
      allowedDocumentRowTypes,
    })

    const validatedRow = (Array.isArray(response?.data) ? response.data : [response?.data]).find(i => i?.id === row.id)

    return validatedRow ? mergeValidatedRow(row, validatedRow, patchFields) : null
  }

  return { importRowsAsync, revalidateRowAsync, isExternalCatalogSource }
}

export const useImportRowWorkflow = (data) => {
  const webSiteStore = useWebSiteStore()
  const { importRowsAsync, isExternalCatalogSource } = useImportRowApi(data)
  const { validationTypes } = useImportRowValidation()

  const isMove = ref(false)
  const isValidating = ref(false)
  const isImporting = ref(false)
  const reviewRows = ref(null)
  const reviewDataSource = ref(null)
  const selectedReviewKeys = ref(null)
  const createNewAutodataProcessing = ref(false)

  const consideredReviewRows = computed(() => {
    const rows = reviewRows.value || []

    if (selectedReviewKeys.value == null) return rows

    const keys = new Set(selectedReviewKeys.value)
    return rows.filter(row => keys.has(row.rowKey))
  })

  const blockingValidationCount = computed(() =>
    consideredReviewRows.value.reduce((count, row) =>
      count + (row.validations || []).filter(validation =>
        validation.validation_control != null &&
        validation.validation_type === validationTypes.ERROR &&
        validation.validation_action == null
      ).length, 0)
  )

  const canImport = computed(() =>
    consideredReviewRows.value.length > 0 && blockingValidationCount.value === 0
  )

  const setSelectedReviewKeys = (keys) => {
    selectedReviewKeys.value = keys ?? null
  }

  const setReviewRows = (rows) => {
    const keyedRows = (rows || []).map(row => ({
      ...row,
      rowKey: row.rowKey ?? `${row.source}-${row.id}`,
    }))
    reviewRows.value = keyedRows
    reviewDataSource.value = new DataSource({
      store: new ArrayStore({ data: keyedRows, key: 'rowKey' }),
    })
  }

  const reset = () => {
    isMove.value = false
    isValidating.value = false
    isImporting.value = false
    reviewRows.value = null
    reviewDataSource.value = null
    selectedReviewKeys.value = null
    createNewAutodataProcessing.value = false
  }

  const importAsync = async(allowedDocumentRowTypes = null) => {
    isImporting.value = true

    try {
      const response = await importRowsAsync({
        sources: buildImportSources(consideredReviewRows.value, webSiteStore.importRowActionEnum.ADD),
        isMove: isMove.value,
        isDryRun: false,
        createNewAutodataProcessing: createNewAutodataProcessing.value,
      })

      const returnedRows = response?.data || []

      if (returnedRows.length) {
        setReviewRows((reviewRows.value || []).map(row =>
          mergeValidatedRow(row, findValidatedRow(returnedRows, row))
        ))
        return { status: 'needs-review' }
      }

      return { status: 'imported' }
    } finally {
      isImporting.value = false
    }
  }

  const validateAndImportAsync = async(selectedRows, isMoveRequest, allowedDocumentRowTypes = null, shouldCreateNewAutodataProcessing = false) => {
    isMove.value = Boolean(isMoveRequest)
    createNewAutodataProcessing.value = shouldCreateNewAutodataProcessing
    isValidating.value = true

    try {
      const response = await importRowsAsync({
        sources: buildSelectionSources(selectedRows, isExternalCatalogSource),
        isMove: isMove.value,
        isDryRun: true,
        allowedDocumentRowTypes,
      })

      const rows = selectedRows.map(row =>
        mergeValidatedRow(row, findValidatedRow(response?.data, row))
      )

      setReviewRows(rows)

      if (!hasValidationControls(rows)) {
        return await importAsync(allowedDocumentRowTypes)
      }

      return { status: 'needs-review' }
    } finally {
      isValidating.value = false
    }
  }

  const replaceReviewRowAsync = async(updatedRow) => {
    if (!updatedRow?.rowKey || !reviewRows.value) return

    reviewRows.value = reviewRows.value.map(row =>
      row.rowKey === updatedRow.rowKey ? { ...row, ...updatedRow } : row
    )

    const store = reviewDataSource.value?.store?.()

    if (store) {
      await store.update(updatedRow.rowKey, { ...updatedRow })
      await reviewDataSource.value.reload()
    }
  }

  return {
    isMove,
    isValidating,
    isImporting,
    reviewDataSource,
    blockingValidationCount,
    canImport,
    validateAndImportAsync,
    importAsync,
    replaceReviewRowAsync,
    setSelectedReviewKeys,
    reset,
  }
}
